import { useTheme } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Chip,
  Avatar,
  Divider,
  Paper,
  TextField,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";
import FileService from "../services/doctorServices/FileService";
import PatientService from "../services/doctorServices/patientService";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ModernDatePicker from "./ModernDatePicker";

export default function AddToNewFilePatient({
  open,
  onClose,
  patientId,
  onApplyFiles,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('patients');
  const fileService = useMemo(() => new FileService(), []);
  const patientService = useMemo(() => new PatientService(), []);
  const [patientInfo, setPatientInfo] = useState({});
  const [files, setFiles] = useState([]);
  const [patientFiles, setPatientFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [treatmentStartDates, setTreatmentStartDates] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Bugünün tarihini YYYY-MM-DD formatında al (minimum tarih için)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 1 ay öncesinin tarihini YYYY-MM-DD formatında al (minimum tarih için)
  const getOneMonthAgoDate = () => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const year = oneMonthAgo.getFullYear();
    const month = String(oneMonthAgo.getMonth() + 1).padStart(2, '0');
    const day = String(oneMonthAgo.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!open || !patientId) return;

    const fetchAllFiles = async () => {
      try {
        const response = await fileService.getAllFiles();
        setFiles(response || []);
      } catch (err) {
        console.error("Files could not be fetched:", err);
      }
    };

    const getPatientInfo = async () => {
      try {
        const response = await patientService.getPatientInfo(patientId);
        const existingFileIds = response?.patient?.Files?.map((file) => file.id) || [];
        setPatientFiles(existingFileIds);
        setPatientInfo(response?.patient || {});
      } catch (e) {
        console.error("Patient info could not be fetched:", e);
      }
    };

    fetchAllFiles();
    getPatientInfo();
  }, [open, patientId, fileService, patientService]);

  const handleApply = async () => {
    if (selectedFiles.length === 0) return;
    
    // Validate treatment start dates
    const missingDates = [];
    const pastDates = [];
    const oneMonthAgo = getOneMonthAgoDate();
    
    selectedFiles.forEach((fileId) => {
      const date = treatmentStartDates[fileId];
      const file = files.find((f) => f.id === fileId);
      
      if (!date || date.trim() === "") {
        missingDates.push(file?.name || fileId);
      } else if (date < oneMonthAgo) {
        pastDates.push(file?.name || fileId);
      }
    });
    
    if (missingDates.length > 0) {
      alert(t('pleaseEnterTreatmentStartDate', { files: missingDates.join(", ") }));
      return;
    }
    
    if (pastDates.length > 0) {
      alert(t('treatmentStartDatePastErrorFiles', { files: pastDates.join(", ") }));
      return;
    }
    
    setLoading(true);
    try {
      const updatedFileIds = [...new Set([...patientFiles, ...selectedFiles])];

      // Build treatmentStartDates object for newly added files only
      const treatmentStartDatesObj = {};
      selectedFiles.forEach((fileId) => {
        if (treatmentStartDates[fileId]) {
          treatmentStartDatesObj[fileId] = treatmentStartDates[fileId];
        }
      });

      const updatedPatient = await patientService.updatePatient(patientId, {
        fileIds: updatedFileIds,
        treatmentStartDates: treatmentStartDatesObj,
      });

      if (onApplyFiles) {
        onApplyFiles(updatedPatient);
      }
      setSelectedFiles([]);
      setTreatmentStartDates({});
      onClose();
    } catch (error) {
      console.error("Error updating patient:", error);
      if (error.response?.status === 403) {
        return;
      }
      alert(error.response?.data?.message || t('errorUpdatingPatient'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setTreatmentStartDates({});
    onClose();
  };

  const availableFiles = useMemo(() => {
    return files.filter((file) => !patientFiles.includes(file.id));
  }, [files, patientFiles]);

  const selectedFileNames = useMemo(() => {
    return selectedFiles
      .map((id) => files.find((f) => f.id === id)?.name)
      .filter(Boolean);
  }, [selectedFiles, files]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.blueAccent[800]} 100%)`,
          boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
          border: `1px solid ${colors.primary[300]}55`,
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
          borderBottom: `2px solid ${colors.blueAccent[500]}40`,
          py: 3,
          px: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
              boxShadow: `0 8px 20px ${colors.primary[900]}40`,
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.grey[100],
                letterSpacing: 0.3,
                mb: 0.5,
              }}
            >
              {t('addPatientToFiles')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[300], opacity: 0.9 }}
            >
              {t('selectFilesToAssociate')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          p: 0,
        }}
      >
        <Box sx={{ p: 4 }}>
          {/* Info Box */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${colors.blueAccent[800]}30 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.blueAccent[500]}40`,
              boxShadow: `0 8px 24px rgba(15, 23, 42, 0.2)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <AddCircleOutlineIcon
                sx={{
                  color: colors.blueAccent[300],
                  fontSize: 28,
                  mt: 0.5,
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {t('selectFiles')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.grey[300], lineHeight: 1.7 }}
                >
                  {t('selectFilesDescription')}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Form Control */}
          <FormControl fullWidth>
            <InputLabel
              id="file-multi-select-label"
              sx={{
                color: colors.grey[300],
                "&.Mui-focused": {
                  color: colors.blueAccent[300],
                },
              }}
            >
              {t('availableFiles')}
            </InputLabel>
            <Select
              labelId="file-multi-select-label"
              multiple
              value={selectedFiles}
              onChange={(e) => {
                const newSelectedFiles = e.target.value;
                setSelectedFiles(newSelectedFiles);
                
                // Update treatmentStartDates when files change
                const newTreatmentDates = { ...treatmentStartDates };
                
                // Remove dates for unselected files
                Object.keys(newTreatmentDates).forEach((fileId) => {
                  if (!newSelectedFiles.includes(fileId)) {
                    delete newTreatmentDates[fileId];
                  }
                });
                
                // Add empty dates for newly selected files
                newSelectedFiles.forEach((fileId) => {
                  if (!newTreatmentDates[fileId]) {
                    newTreatmentDates[fileId] = "";
                  }
                });
                
                setTreatmentStartDates(newTreatmentDates);
              }}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((id) => {
                    const file = files.find((f) => f.id === id);
                    return file ? (
                      <Chip
                        key={id}
                        label={file.name}
                        size="small"
                        sx={{
                          backgroundColor: colors.greenAccent[700],
                          color: "white",
                          fontWeight: 600,
                          "& .MuiChip-deleteIcon": {
                            color: "white",
                          },
                        }}
                      />
                    ) : null;
                  })}
                </Box>
              )}
              sx={{
                backgroundColor: colors.primary[600],
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${colors.blueAccent[500]}40`,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${colors.blueAccent[500]}60`,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[500],
                  borderWidth: 2,
                },
                "& .MuiSelect-select": {
                  color: colors.grey[100],
                  py: 2,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.primary[600],
                    borderRadius: 2,
                    mt: 1,
                    boxShadow: `0 8px 24px rgba(15, 23, 42, 0.3)`,
                    border: `1px solid ${colors.blueAccent[500]}40`,
                    "& .MuiMenuItem-root": {
                      color: colors.grey[100],
                      "&:hover": {
                        backgroundColor: `${colors.blueAccent[800]}40`,
                      },
                      "&.Mui-selected": {
                        backgroundColor: `${colors.greenAccent[700]}40`,
                        "&:hover": {
                          backgroundColor: `${colors.greenAccent[700]}60`,
                        },
                      },
                      "&.Mui-disabled": {
                        color: colors.grey[600],
                        opacity: 0.5,
                      },
                    },
                  },
                },
              }}
            >
              {availableFiles.length === 0 ? (
                <MenuItem disabled>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.grey[500], fontStyle: "italic" }}
                  >
                    {t('noAvailableFiles')}
                  </Typography>
                </MenuItem>
              ) : (
                availableFiles.map((file) => (
                  <MenuItem key={file.id} value={file.id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      <FolderOpenIcon
                        sx={{
                          color: colors.blueAccent[300],
                          fontSize: 20,
                        }}
                      />
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {file.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <>
              <Paper
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.greenAccent[800]}30 0%, ${colors.blueAccent[800]}20 100%)`,
                  border: `1px solid ${colors.greenAccent[500]}40`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <CheckCircleIcon
                    sx={{ color: colors.greenAccent[400], fontSize: 22 }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: colors.grey[100],
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('selectedFiles')} ({selectedFiles.length})
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {selectedFileNames.map((name, index) => (
                    <Chip
                      key={index}
                      label={name}
                      size="small"
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        backgroundColor: colors.greenAccent[700],
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              </Paper>

              {/* Treatment Start Dates */}
              <Paper
                sx={{
                  mt: 3,
                  p: 3,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.blueAccent[800]}30 0%, ${colors.primary[500]} 100%)`,
                  border: `1px solid ${colors.blueAccent[500]}40`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 600,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {t('treatmentStartDates')}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {selectedFiles.map((fileId) => {
                    const file = files.find((f) => f.id === fileId);
                    if (!file) return null;
                    
                    return (
                      <Box key={fileId}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.grey[300],
                            mb: 1,
                            fontWeight: 500,
                          }}
                        >
                          {file.name}
                        </Typography>
                        <ModernDatePicker
                          value={treatmentStartDates[fileId] || ""}
                          onChange={(selectedDate) => {
                            const oneMonthAgo = getOneMonthAgoDate();
                            
                            // Geçmiş tarih kontrolü (1 ay öncesine kadar izin ver)
                            if (selectedDate && selectedDate < oneMonthAgo) {
                              alert(t('treatmentStartDatePastError'));
                              return;
                            }
                            
                            setTreatmentStartDates({
                              ...treatmentStartDates,
                              [fileId]: selectedDate,
                            });
                          }}
                          minDate={getOneMonthAgoDate()}
                          placeholder={t('selectTreatmentStartDate')}
                          error={!treatmentStartDates[fileId]}
                          helperText={!treatmentStartDates[fileId] ? t('treatmentStartDateRequired') : ""}
                          required
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </>
          )}
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: `${colors.grey[800]}30` }} />

      <DialogActions
        sx={{
          background: `linear-gradient(135deg, ${colors.blueAccent[800]} 0%, ${colors.primary[500]} 100%)`,
          px: 4,
          py: 2.5,
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          startIcon={<CancelIcon />}
          sx={{
            backgroundColor: `${colors.redAccent[700]}40`,
            color: colors.grey[100],
            border: `1px solid ${colors.redAccent[500]}40`,
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.95rem",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: `${colors.redAccent[600]}60`,
              transform: "translateY(-2px)",
              boxShadow: `0 4px 12px ${colors.redAccent[500]}40`,
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={selectedFiles.length === 0 || loading}
          startIcon={<CheckCircleIcon />}
          sx={{
            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[700]} 100%)`,
            color: "white",
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.95rem",
            boxShadow: `0 8px 20px rgba(76, 206, 172, 0.3)`,
            transition: "all 0.3s ease",
            "&:hover": {
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[800]} 100%)`,
              transform: "translateY(-2px)",
              boxShadow: `0 12px 28px rgba(76, 206, 172, 0.4)`,
            },
            "&:disabled": {
              background: `${colors.grey[700]}40`,
              color: colors.grey[500],
              boxShadow: "none",
            },
          }}
        >
          {loading ? t('applying') : `${t('apply')} (${selectedFiles.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
